import { Avatar, Box, Button, Heading, Icon, List, Paragraph } from '@zillow/constellation-10';
import { IconHouseFilled, IconTrendingFilled, IconTrophyFilled } from '@zillow/constellation-icons';

import { useSelector } from '../../../../../../selectors/useSelector';
import PropertyConstants from '../../../../../../constants/PropertyConstants';

import { getFreemiumProductByType } from '../../../../../../utils/upsellUtils';

import PayAndPublishCSEvents from '../../../../../../model/ClickstreamEvent/PayAndPublishCSEvents';

import { CheckListItem } from '../CheckListItem';

import { cardAvatarStyles, cardBodyStyles, cardStyles } from '../styles';

import type { ProductTypeId } from '..';

export const UpsellCard = ({ type, onPublishClick }: { type: ProductTypeId; onPublishClick: () => void }) => {
  const freemiumProducts = useSelector((state) => state.properties.currentProperty.freemium.products);
  const premiumProduct = getFreemiumProductByType(freemiumProducts, PropertyConstants.PRODUCT_TYPE_NAME_TO_ID.PREMIUM);
  const premiumPrice = premiumProduct?.price;
  const premiumDuration = premiumProduct?.duration;

  return (
    <Box css={cardStyles} data-testid="PremiumUpsellCard">
      <Avatar.Root
        css={cardAvatarStyles.raw({ isPremium: type === PropertyConstants.PRODUCT_TYPE_NAME_TO_ID.PREMIUM })}
      >
        <Avatar.Icon
          css={{ color: type === PropertyConstants.PRODUCT_TYPE_NAME_TO_ID.PREMIUM ? 'yellow.500' : 'gray.600' }}
        >
          {type === PropertyConstants.PRODUCT_TYPE_NAME_TO_ID.PREMIUM ? <IconTrophyFilled /> : <IconHouseFilled />}
        </Avatar.Icon>
      </Avatar.Root>
      <Heading level={3} textStyle="heading-xs">
        {type === PropertyConstants.PRODUCT_TYPE_NAME_TO_ID.PREMIUM ? 'Premium' : 'Free'}
      </Heading>
      {type === PropertyConstants.PRODUCT_TYPE_NAME_TO_ID.PREMIUM ? (
        <Paragraph css={{ color: 'yellow.600', marginTop: '200', marginBottom: '400' }} textStyle="body-sm-bold">
          <Icon role="img">
            <IconTrendingFilled />
          </Icon>
          {premiumDuration && ` Up to ${premiumDuration} of benefits`}
        </Paragraph>
      ) : (
        <Paragraph css={{ marginTop: '200', marginBottom: '400' }}>Listing essentials</Paragraph>
      )}
      <Box css={cardBodyStyles}>
        {type === PropertyConstants.PRODUCT_TYPE_NAME_TO_ID.PREMIUM ? (
          <>
            <Paragraph textStyle="body-sm" css={{ marginBottom: '200' }}>
              Everything in the <strong>Free package</strong>, plus:
            </Paragraph>
            <List.Root appearance="none">
              <CheckListItem>
                <strong>Prioritized placement</strong> above free listings
              </CheckListItem>
              <CheckListItem>
                <strong>Pricing insights</strong> to know your market
              </CheckListItem>
              <CheckListItem>
                <strong>Listing recommendations</strong> with customized tips
              </CheckListItem>
              <CheckListItem>
                <strong>Priority support</strong> with fast answers to your questions
              </CheckListItem>
            </List.Root>
          </>
        ) : (
          <List.Root appearance="none">
            <CheckListItem>Syndication to Trulia, Hotpads, and Zillow</CheckListItem>
            <CheckListItem>Online applications, lease signing, & payments</CheckListItem>
            <CheckListItem>App/messaging platform</CheckListItem>
          </List.Root>
        )}
      </Box>
      <Button
        emphasis={type === PropertyConstants.PRODUCT_TYPE_NAME_TO_ID.PREMIUM ? 'filled' : 'outlined'}
        css={{ margin: '600', width: 'calc(100% - {spacing.1200})' }}
        onClick={onPublishClick}
        data-eventid={
          type === PropertyConstants.PRODUCT_TYPE_NAME_TO_ID.PREMIUM
            ? PayAndPublishCSEvents.StartActivatePremiumBottom.data_event_id
            : PayAndPublishCSEvents.StartActivateFreemiumBottom.data_event_id
        }
      >
        Publish
        {type === PropertyConstants.PRODUCT_TYPE_NAME_TO_ID.PREMIUM ? (
          <> with premium{Boolean(premiumPrice) && ` for ${premiumPrice}`}</>
        ) : (
          ' for free'
        )}
      </Button>
    </Box>
  );
};
